import { useState, useEffect, useCallback, createContext, useContext } from 'react';

const CONFIG_KEY = 'kp_config';

export type CTAType = 'none' | 'webinar' | 'info' | 'check' | 'call' | 'priority_call' | 'special' | 'waitlist' | 'sleep-scan' | 'newsletter' | 'download' | 'whatsapp';

export interface KPConfig {
  // Score thresholds
  thresholds: {
    low_max: number;      // default: 2
    medium_max: number;   // default: 5
    high_min: number;     // default: 6
  };
  
  // CTA mapping per score band
  ctaMapping: {
    low: CTAType;
    medium: CTAType;
    high: CTAType;
  };
  
  // Time-based overrides
  timeOverrides: {
    sunday: CTAType;
    nightHours: CTAType;  // 22-06
    nightEnabled: boolean;
    sundayEnabled: boolean;
  };
  
  // Device overrides
  deviceOverrides: {
    mobile: CTAType | 'default';
    desktop: CTAType | 'default';
    mobileEnabled: boolean;
    desktopEnabled: boolean;
  };
  
  // Dashboard settings
  dashboard: {
    debugVisible: boolean;
  };
}

export const DEFAULT_CONFIG: KPConfig = {
  thresholds: {
    low_max: 2,
    medium_max: 5,
    high_min: 6,
  },
  ctaMapping: {
    low: 'webinar',
    medium: 'check',
    high: 'call',
  },
  timeOverrides: {
    sunday: 'special',
    nightHours: 'info',
    nightEnabled: false,
    sundayEnabled: true,
  },
  deviceOverrides: {
    mobile: 'sleep-scan',
    desktop: 'default',
    mobileEnabled: true,
    desktopEnabled: false,
  },
  dashboard: {
    debugVisible: false,
  },
};

const loadConfig = (): KPConfig => {
  try {
    const stored = localStorage.getItem(CONFIG_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to ensure all fields exist
      return {
        ...DEFAULT_CONFIG,
        ...parsed,
        thresholds: { ...DEFAULT_CONFIG.thresholds, ...parsed.thresholds },
        ctaMapping: { ...DEFAULT_CONFIG.ctaMapping, ...parsed.ctaMapping },
        timeOverrides: { ...DEFAULT_CONFIG.timeOverrides, ...parsed.timeOverrides },
        deviceOverrides: { ...DEFAULT_CONFIG.deviceOverrides, ...parsed.deviceOverrides },
        dashboard: { ...DEFAULT_CONFIG.dashboard, ...parsed.dashboard },
      };
    }
  } catch {
    // Ignore parse errors
  }
  return { ...DEFAULT_CONFIG };
};

const saveConfig = (config: KPConfig): void => {
  try {
    localStorage.setItem(CONFIG_KEY, JSON.stringify(config));
    // Update global object
    if (typeof window !== 'undefined') {
      (window as any).kp_config = config;
    }
  } catch {
    // Ignore storage errors
  }
};

interface KPConfigContextValue {
  config: KPConfig;
  updateConfig: (updates: Partial<KPConfig>) => void;
  updateThresholds: (thresholds: Partial<KPConfig['thresholds']>) => void;
  updateCTAMapping: (mapping: Partial<KPConfig['ctaMapping']>) => void;
  updateTimeOverrides: (overrides: Partial<KPConfig['timeOverrides']>) => void;
  updateDeviceOverrides: (overrides: Partial<KPConfig['deviceOverrides']>) => void;
  resetToDefaults: () => void;
}

const KPConfigContext = createContext<KPConfigContextValue | null>(null);

export const KPConfigProvider = ({ children }: { children: React.ReactNode }) => {
  const [config, setConfig] = useState<KPConfig>(() => loadConfig());

  // Expose to window on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).kp_config = config;
    }
  }, [config]);

  const updateConfig = useCallback((updates: Partial<KPConfig>) => {
    setConfig(prev => {
      const newConfig = { ...prev, ...updates };
      saveConfig(newConfig);
      return newConfig;
    });
  }, []);

  const updateThresholds = useCallback((thresholds: Partial<KPConfig['thresholds']>) => {
    setConfig(prev => {
      const newConfig = {
        ...prev,
        thresholds: { ...prev.thresholds, ...thresholds },
      };
      saveConfig(newConfig);
      return newConfig;
    });
  }, []);

  const updateCTAMapping = useCallback((mapping: Partial<KPConfig['ctaMapping']>) => {
    setConfig(prev => {
      const newConfig = {
        ...prev,
        ctaMapping: { ...prev.ctaMapping, ...mapping },
      };
      saveConfig(newConfig);
      return newConfig;
    });
  }, []);

  const updateTimeOverrides = useCallback((overrides: Partial<KPConfig['timeOverrides']>) => {
    setConfig(prev => {
      const newConfig = {
        ...prev,
        timeOverrides: { ...prev.timeOverrides, ...overrides },
      };
      saveConfig(newConfig);
      return newConfig;
    });
  }, []);

  const updateDeviceOverrides = useCallback((overrides: Partial<KPConfig['deviceOverrides']>) => {
    setConfig(prev => {
      const newConfig = {
        ...prev,
        deviceOverrides: { ...prev.deviceOverrides, ...overrides },
      };
      saveConfig(newConfig);
      return newConfig;
    });
  }, []);

  const resetToDefaults = useCallback(() => {
    setConfig(DEFAULT_CONFIG);
    saveConfig(DEFAULT_CONFIG);
  }, []);

  return (
    <KPConfigContext.Provider
      value={{
        config,
        updateConfig,
        updateThresholds,
        updateCTAMapping,
        updateTimeOverrides,
        updateDeviceOverrides,
        resetToDefaults,
      }}
    >
      {children}
    </KPConfigContext.Provider>
  );
};

export const useKPConfig = (): KPConfigContextValue => {
  const context = useContext(KPConfigContext);
  if (!context) {
    // Return default config for components outside provider
    return {
      config: loadConfig(),
      updateConfig: () => {},
      updateThresholds: () => {},
      updateCTAMapping: () => {},
      updateTimeOverrides: () => {},
      updateDeviceOverrides: () => {},
      resetToDefaults: () => {},
    };
  }
  return context;
};

// Global read-only access
export const getKPConfig = (): KPConfig => loadConfig();

// Determine score band based on config
export const getScoreBand = (score: number, config: KPConfig): 'low' | 'medium' | 'high' => {
  if (score <= config.thresholds.low_max) return 'low';
  if (score <= config.thresholds.medium_max) return 'medium';
  return 'high';
};

// Get CTA decision based on all factors
export const getCTADecision = (
  score: number,
  dayOfWeek: number,
  hour: number,
  isMobile: boolean,
  isDesktop: boolean,
  config: KPConfig
): { cta: CTAType; reason: string } => {
  // Time overrides first
  if (config.timeOverrides.sundayEnabled && dayOfWeek === 0) {
    return { cta: config.timeOverrides.sunday, reason: 'Sunday override' };
  }
  
  if (config.timeOverrides.nightEnabled && (hour >= 22 || hour < 6)) {
    return { cta: config.timeOverrides.nightHours, reason: 'Night hours override' };
  }
  
  // Device overrides
  if (config.deviceOverrides.mobileEnabled && isMobile && config.deviceOverrides.mobile !== 'default') {
    return { cta: config.deviceOverrides.mobile as CTAType, reason: 'Mobile device override' };
  }
  
  if (config.deviceOverrides.desktopEnabled && isDesktop && config.deviceOverrides.desktop !== 'default') {
    return { cta: config.deviceOverrides.desktop as CTAType, reason: 'Desktop device override' };
  }
  
  // Score-based decision
  const band = getScoreBand(score, config);
  return { cta: config.ctaMapping[band], reason: `Score band: ${band} (${score} points)` };
};

// Initialize global object
if (typeof window !== 'undefined') {
  (window as any).kp_config = loadConfig();
}

export default useKPConfig;
